/**
 * OKR 前端路由表。
 * 主布局在 Index 下（含 Tab）；效能/主管/Boss 子路由与 legacy 项目/管理模块并存。
 */
import Vue from "vue";
import Router from "vue-router";
import { canAccessRoute, hasPerm, hasRole, isBossDashboardAccessible } from '@/utils/perm';
import Index from "@/views/Index";
import Home from "@/views/home/HomeEntry";
import Gantt from "@/views/home/Gantt";
import Rank from "@/views/home/Rank";
import Leader from "@/views/home/HomeLeader";
import Message from "@/views/home/Message";
import MessageDetail from "@/views/home/MessageDetail";

import Project from "@/views/project/Project";
import ProjectJoin from "@/views/project/ProjectJoin";
import ProjectDetail from "@/views/project/ProjectDetail";
import Review from "@/views/project/Review";
import ReviewStart from "@/views/project/ReviewStart";
import ReviewJoin from "@/views/project/ReviewJoin";
import ReviewEnd from "@/views/project/ReviewEnd";

import Add from "@/views/add/Add";
import AddHelp from "@/views/add/AddHelp";
import AddProject from "@/views/add/AddProject";

import Manage from "@/views/manage/Manage";
import WeekABC from "@/views/manage/WeekABC";
import Log from "@/views/manage/Log";
import Help from "@/views/manage/Help";
import MWeekABC from "@/views/manage/MWeekABC";

import My from "@/views/my/My";
import Mail from "@/views/my/Mail";
// import Structure from '@/views/my/Structure'
// import Duty from '@/views/my/Duty'
import Cost from "@/views/my/Cost";
import Income from "@/views/my/Income";
import Info from "@/views/my/Info";

import AddSatisfaction from "@/views/satisfaction/Add";
import Satisfaction from "@/views/satisfaction/Index";
import DetailSatisfaction from "@/views/satisfaction/Detail";

import Login from "@/views/Login";
import Efficiency from "@/views/efficiency/Efficiency";
import EfficiencyLog from "@/views/efficiency/WorklogForm";
import EfficiencyLeaderboard from "@/views/efficiency/Leaderboard";
import EfficiencyHistory from "@/views/efficiency/History";
import SupervisorHome from "@/views/efficiency/supervisor/Home";
import SupervisorReviews from "@/views/efficiency/supervisor/Reviews";
import SupervisorReview from "@/views/efficiency/supervisor/Review";
import SupervisorMonitor from "@/views/efficiency/supervisor/Monitor";
import SupervisorDiagnosis from "@/views/efficiency/supervisor/Diagnosis";
import SupervisorWeeklyBrief from "@/views/efficiency/supervisor/WeeklyBrief";
import SupervisorCorrections from "@/views/efficiency/supervisor/Corrections";
import BossHeatmap from "@/views/boss/Heatmap";
import BossReport from "@/views/boss/Report";
import BossRationality from "@/views/boss/Rationality";
import BossTasks from "@/views/boss/Tasks";
import BossKnowledgeUpload from "@/views/boss/KnowledgeUpload";
import BossPerformanceConfig from "@/views/boss/PerformanceConfig";
import BossDutyAdjust from "@/views/boss/DutyAdjust";
import BossGovernanceCenter from "@/views/boss/GovernanceCenter";
import BossProjectPortfolio from "@/views/boss/ProjectPortfolio";
import BossProjectDetail from "@/views/boss/ProjectDetail";
import BossProjectTaskPlanning from "@/views/boss/ProjectTaskPlanning";
import BossProjectValueChecklist from "@/views/boss/ProjectValueChecklist";
import BossProjectValueRanking from "@/views/boss/ProjectValueRanking";
import BossStrategyExecution from "@/views/boss/StrategyExecution";
import BossHomeOverview from "@/views/boss/HomeOverview";
import BossLogRankings from "@/views/boss/LogRankings";
import BossFullEffect from "@/views/boss/FullEffect";
import BossDepartmentData from "@/views/boss/DepartmentData";
import BossCustomerData from "@/views/boss/CustomerData";
import BossCustomerRank from "@/views/boss/CustomerRank";
import BossCustomerList from "@/views/boss/CustomerList";
import BossCustomerAdd from "@/views/boss/CustomerAdd";
import BossIdentityRoleList from "@/views/boss/identity/RoleList.vue";
import BossIdentityRoleEdit from "@/views/boss/identity/RoleEdit.vue";
import BossIdentityEmployeeRoles from "@/views/boss/identity/EmployeeRoles.vue";
import BossIdentityEmployeeAssign from "@/views/boss/identity/EmployeeAssign.vue";
import BossIdentityPermissionCatalog from "@/views/boss/identity/PermissionCatalog.vue";
import BossAbilityCards from "@/views/boss/AbilityCards";
import EfficiencyTasks from "@/views/efficiency/Tasks";
import EfficiencyTaskDetail from "@/views/efficiency/TaskDetail";
import EfficiencyCorrections from "@/views/efficiency/Corrections";
import EfficiencyCorrectionDetail from "@/views/efficiency/CorrectionDetail";
import EfficiencyWorkPoints from "@/views/efficiency/WorkPoints";
import EfficiencyGrowth from "@/views/efficiency/Growth";
import EfficiencyWorkPointGenerate from "@/views/efficiency/WorkPointGenerate";
import StrategyActionMap from "@/views/efficiency/supervisor/StrategyActionMap";
import SupervisorStrategyWorkbench from "@/views/efficiency/supervisor/StrategyWorkbench";
import brandRoutes from "./brand";

Vue.use(Router);

const router = new Router({
  routes: [
    {
      path: "/",
      name: "Index",
      component: Index,
      redirect: "home",
      children: [
        {
          path: "/knowledge",
          name: "knowledge",
          component: () => import("@/views/knowledge/knowledge.vue"),
          meta: {
            keepAlive: true,
            permCode: ['knowledge.document.read']
          }
        },
        {
          path: "/home",
          name: "Home",
          component: Home,
          meta: {
            keepAlive: true
          }
        },
        {
          path: "/project",
          name: "Project",
          component: Project,
          meta: {
            keepAlive: true
          }
        },
        {
          path: "/add",
          name: "Add",
          component: Add
        },
        {
          path: "/manage",
          name: "Manage",
          component: Manage,
          redirect: "weekABC",
          children: [
            {
              path: "/weekABC",
              name: "WeekABC",
              component: WeekABC
            },
            {
              path: "/MWeekABC",
              name: "MWeekABC",
              component: MWeekABC
            },
            {
              path: "/log",
              name: "Log",
              component: Log
            },
            {
              path: "/help",
              name: "Help",
              component: Help
            }
          ]
        },
        {
          path: "/satisfaction",
          name: "Satisfaction",
          component: Satisfaction,
          meta: {
            keepAlive: true
          }
        },
        {
          path: "/my",
          name: "My",
          component: My
        },
        { path: "/boss/heatmap", name: "BossHeatmap", component: BossHeatmap, meta: { permCode: ['boss.heatmap.read', 'analytics.tenant.read'] } },
        { path: "/boss/report", name: "BossReport", component: BossReport, meta: { permCode: ['analytics.tenant.read', 'boss.dashboard.read'] } },
        { path: "/boss/rationality", name: "BossRationality", component: BossRationality, meta: { permCode: ['boss.rationality.read', 'boss.dashboard.read'] } },
        { path: "/boss/tasks", name: "BossTasks", component: BossTasks, meta: { permCode: ['boss.task.read', 'boss.dashboard.read'] } },
        { path: "/boss/performance-config", name: "BossPerformanceConfig", component: BossPerformanceConfig, meta: { permCode: ['boss.performance.manage', 'efficiency.worklog.read'] } },
        { path: "/boss/duty-adjust", name: "BossDutyAdjust", component: BossDutyAdjust, meta: { permCode: ['identity.employee.manage', 'supervisor.review.manage'] } },
        { path: "/boss/knowledge-upload", name: "BossKnowledgeUpload", component: BossKnowledgeUpload, meta: { permCode: ['knowledge.document.manage'] } },
        { path: "/boss/governance", name: "BossGovernanceCenter", component: BossGovernanceCenter, meta: { permCode: ['boss.governance.manage'] } },
        { path: "/boss/projects", name: "BossProjectPortfolio", component: BossProjectPortfolio, meta: { permCode: ['boss.project.read', 'boss.project.manage'] } },
        { path: "/boss/projects/value-ranking", name: "BossProjectValueRanking", component: BossProjectValueRanking, meta: { permCode: ['boss.project.read', 'boss.project.manage'] } },
        { path: "/boss/projects/:id", name: "BossProjectDetail", component: BossProjectDetail, meta: { permCode: ['boss.project.read', 'boss.project.manage'] } },
        { path: "/boss/projects/:id/task-planning", name: "BossProjectTaskPlanning", component: BossProjectTaskPlanning, meta: { permCode: ['boss.project.manage'] } },
        { path: "/boss/projects/:id/value-checklist", name: "BossProjectValueChecklist", component: BossProjectValueChecklist, meta: { permCode: ['boss.project.manage'] } },
        { path: "/boss/strategy-execution", name: "BossStrategyExecution", component: BossStrategyExecution, meta: { permCode: ['boss.governance.manage'] } },
        { path: "/boss/home", name: "BossHomeOverview", component: BossHomeOverview, meta: { permCode: ['boss.dashboard.read', 'boss.dashboard.manage', 'analytics.tenant.read'] } },
        { path: "/boss/log-rankings", name: "BossLogRankings", component: BossLogRankings, meta: { permCode: ['efficiency.worklog.read', 'supervisor.monitor.read'] } },
        { path: "/boss/department-data", name: "BossDepartmentData", component: BossDepartmentData, meta: { permCode: ['analytics.tenant.read', 'analytics.tenant.manage'] } },
        { path: "/boss/customer-data", name: "BossCustomerData", component: BossCustomerData, meta: { permCode: ['analytics.tenant.read', 'boss.customer.read'] } },
        { path: "/boss/customer-rank", name: "BossCustomerRank", component: BossCustomerRank, meta: { permCode: ['analytics.tenant.read', 'boss.customer.read'] } },
        { path: "/boss/customer-list", name: "BossCustomerList", component: BossCustomerList, meta: { permCode: ['boss.customer.read', 'boss.customer.manage'] } },
        { path: "/boss/customer-add", name: "BossCustomerAdd", component: BossCustomerAdd, meta: { permCode: ['boss.customer.manage'] } },
        { path: "/boss/ability-cards", name: "BossAbilityCards", component: BossAbilityCards, meta: { permCode: ['boss.dashboard.read', 'analytics.tenant.read'] } },
        { path: "/boss/full-effect", name: "BossFullEffect", component: BossFullEffect, meta: { permCode: ['analytics.tenant.read', 'analytics.tenant.manage', 'efficiency.worklog.read'] } },
        { path: "/boss/identity/roles", name: "BossIdentityRoleList", component: BossIdentityRoleList, meta: { title: "角色管理", permCode: ['identity.role.read', 'identity.role.manage'] } },
        { path: "/boss/identity/roles/new", name: "BossIdentityRoleNew", component: BossIdentityRoleEdit, meta: { title: "新增角色", permCode: ['identity.role.manage'] } },
        { path: "/boss/identity/roles/:id", name: "BossIdentityRoleEdit", component: BossIdentityRoleEdit, meta: { title: "编辑角色", permCode: ['identity.role.manage'] } },
        { path: "/boss/identity/employees", name: "BossIdentityEmployeeRoles", component: BossIdentityEmployeeRoles, meta: { title: "成员权限", permCode: ['identity.employee.read', 'identity.employee.manage'] } },
        { path: "/boss/identity/employees/:id", name: "BossIdentityEmployeeAssign", component: BossIdentityEmployeeAssign, meta: { title: "分配角色", permCode: ['identity.employee.manage'] } },
        { path: "/boss/identity/permissions", name: "BossIdentityPermissionCatalog", component: BossIdentityPermissionCatalog, meta: { title: "权限目录", permCode: ['system.config.read', 'system.audit.read', 'identity.role.read', 'identity.employee.read', 'boss.dashboard.read'] } },
        // 全效（员工）子页
        {
          path: "/efficiency/leaderboard",
          name: "EfficiencyLeaderboard",
          component: EfficiencyLeaderboard,
          meta: { permCode: ['efficiency.worklog.read'] }
        },
        { path:"/efficiency/history",name:"EfficiencyHistory",component:EfficiencyHistory,meta:{permCode:['efficiency.worklog.read']} },
        { path:"/efficiency/tasks",name:"EfficiencyTasks",component:EfficiencyTasks,meta:{permCode:['efficiency.task.read']} },
        { path:"/efficiency/tasks/:id",name:"EfficiencyTaskDetail",component:EfficiencyTaskDetail,meta:{permCode:['efficiency.task.read']} },
        { path:"/efficiency/corrections",name:"EfficiencyCorrections",component:EfficiencyCorrections,meta:{permCode:['efficiency.correction.read']} },
        { path:"/efficiency/corrections/:id",name:"EfficiencyCorrectionDetail",component:EfficiencyCorrectionDetail,meta:{permCode:['efficiency.correction.read']} },
        { path:"/efficiency/work-points",name:"EfficiencyWorkPoints",component:EfficiencyWorkPoints,meta:{permCode:['efficiency.work-point.read']} },
        { path:"/efficiency/growth",name:"EfficiencyGrowth",component:EfficiencyGrowth,meta:{permCode:['efficiency.growth.read']} },
        { path:"/efficiency/work-points/generate",name:"EfficiencyWorkPointGenerate",component:EfficiencyWorkPointGenerate,meta:{permCode:['efficiency.work-point.manage']} },
        { path:"/efficiency/supervisor/strategy-map",name:"StrategyActionMap",component:StrategyActionMap,meta:{permCode:['supervisor.review.manage','supervisor.monitor.read']} },
        { path:"/efficiency/supervisor/strategy-workbench",name:"SupervisorStrategyWorkbench",component:SupervisorStrategyWorkbench,meta:{permCode:['supervisor.review.manage','supervisor.monitor.read']} },
        { path:"/efficiency/supervisor",name:"SupervisorHome",component:SupervisorHome,meta:{permCode:['supervisor.review.read','supervisor.review.manage','supervisor.monitor.read']} },
        { path:"/efficiency/supervisor/reviews",name:"SupervisorReviews",component:SupervisorReviews,meta:{permCode:['supervisor.review.read','supervisor.review.manage']} },
        { path:"/efficiency/supervisor/reviews/:id",name:"SupervisorReview",component:SupervisorReview,meta:{permCode:['supervisor.review.read','supervisor.review.manage']} },
        { path:"/efficiency/supervisor/monitor",name:"SupervisorMonitor",component:SupervisorMonitor,meta:{permCode:['supervisor.monitor.read']} },
        { path:"/efficiency/supervisor/diagnosis",name:"SupervisorDiagnosis",component:SupervisorDiagnosis,meta:{permCode:['supervisor.monitor.read','supervisor.review.manage']} },
        { path:"/efficiency/supervisor/weekly-brief",name:"SupervisorWeeklyBrief",component:SupervisorWeeklyBrief,meta:{permCode:['supervisor.monitor.read','supervisor.review.read']} },
        { path:"/efficiency/supervisor/corrections",name:"SupervisorCorrections",component:SupervisorCorrections,meta:{permCode:['supervisor.review.read','supervisor.review.manage']} },
        { path:"/efficiency/finance/quantitative",name:"FinanceQuantitativeReviews",component:()=>import("@/views/efficiency/finance/QuantitativeReviews.vue"),meta:{permCode:['finance.quantitative.read','finance.quantitative.manage']} },
        { path:"/efficiency/finance/quantitative/:id",name:"FinanceQuantitativeReview",component:()=>import("@/views/efficiency/finance/QuantitativeReview.vue"),meta:{permCode:['finance.quantitative.read','finance.quantitative.manage']} },
        { path:"/efficiency/finance/settlements",name:"FinanceSettlements",component:()=>import("@/views/efficiency/finance/Settlements.vue"),meta:{permCode:['finance.settlement.read','finance.settlement.manage']} },
        { path:"/efficiency/boss/all-worklogs",name:"EfficiencyAllWorklogs",component:()=>import("@/views/efficiency/boss/AllWorklogs.vue"),meta:{title:"全员日志",permCode:['efficiency.worklog.read','efficiency.worklog.manage']}},
        {
          path: "/efficiency",
          name: "Efficiency",
          component: Efficiency,
          meta: { keepAlive: true, permCode: ['efficiency.worklog.read'] }
        },
        ...brandRoutes
      ]
    },
    {
      path: "/login",
      name: "Login",
      component: Login
    },
    {
      path: "/efficiency/log",
      name: "EfficiencyLog",
      component: EfficiencyLog,
      meta: { permCode: ['efficiency.worklog.read'] }
    },
    // 以下路由在 Index 布局外（全屏页）
    {
      path: "/rank",
      name: "Rank",
      component: Rank
    },
    {
      path: "/message",
      name: "Message",
      component: Message,
      meta: {
        keepAlive: true
      }
    },
    {
      path: "/leader",
      name: "Leader",
      component: Leader,
      // 经营统计页：含全公司/部门成本收入及员工明细，仅管理层可见（团队或全公司报表权限任一）
      meta: { permCode: ['analytics.team.read', 'analytics.tenant.read'] }
    },
    {
      path: "/messageDetail/:sysno",
      name: "MessageDetail",
      component: MessageDetail
    },
    {
      path: "/gantt",
      name: "Gantt",
      component: Gantt
    },
    // 项目
    {
      path: "/projectJoin",
      name: "ProjectJoin",
      component: ProjectJoin
    },
    {
      path: "/projectDetail/:sysno",
      name: "ProjectDetail",
      component: ProjectDetail
    },
    {
      path: "/review",
      name: "Review",
      component: Review,
      meta: {
        keepAlive: true
      }
    },
    {
      path: "/reviewStart/:sysno",
      name: "ReviewStart",
      component: ReviewStart
    },
    {
      path: "/reviewJoin/:sysno",
      name: "ReviewJoin",
      component: ReviewJoin
    },
    {
      path: "/reviewEnd/:sysno",
      name: "ReviewEnd",
      component: ReviewEnd
    },
    // 创建
    {
      path: "/addHelp/:id",
      name: "AddHelp",
      component: AddHelp
    },
    {
      path: "/addProject",
      name: "AddProject",
      component: AddProject
    },
    // 管理
    // {
    //   path: '/MWeekABC',
    //   name: 'MWeekABC',
    //   component: MWeekABC
    // },
    // 我的
    {
      path: "/mail",
      name: "Mail",
      component: Mail,
      meta: { permCode: ['identity.mail.read'] }
    },
    {
      path: "/mailDetail/:sysno",
      name: "MailDetail",
      component: () => import("@/views/my/MailDetail"),
      meta: { permCode: ['identity.mail.read'] }
    },
    {
      path: "/structure",
      name: "Structure",
      component: () => import("@/views/my/Structure"),
      meta: { permCode: ['identity.organization.read'] }
    },
    {
      path: "/duty",
      name: "Duty",
      component: () => import("@/views/my/Duty"),
      meta: { permCode: ['kpi.duty.read'] }
    },
    {
      path: "/cost",
      name: "Cost",
      component: Cost
    },
    {
      path: "/income",
      name: "Income",
      component: Income
    },
    {
      path: "/info",
      name: "Info",
      component: Info
    },
    {
      path: "/password",
      name: "Password",
      component: () => import("@/views/my/Password")
    },
    {
      path: "/setting",
      name: "Setting",
      component: () => import("@/views/my/Setting")
    },

    {
      path: "/satisfaction/add",
      name: "AddSatisfaction",
      component: AddSatisfaction
    },
    {
      path: "/satisfaction/detail",
      name: "DetailSatisfaction",
      component: DetailSatisfaction
    }
  ],
  scrollBehavior(to, from, savedPosition) {
    // keep-alive 返回缓存页面后记录浏览位置
    if (savedPosition && to.meta.keepAlive) {
      window.targetPosition = savedPosition;
      console.log(savedPosition);
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }

    // 异步滚动操作
    // return new Promise((resolve) => {
    //   setTimeout(() => {
    //     if (savedPosition && to.meta.keepAlive) {
    //       return savedPosition;
    //     }
    //     else{
    //       return { x: 0, y: 0 }
    //     }
    //   }, 0);
    // });
  }
});

// 解析 efficiency 入口 URL 的 data 查询串，并逐字段写入 localStorage
function applyEfficiencyLoginDataFromHash() {
  const hash = window.location.hash || '';
  const m = hash.match(/[?&]data=([^&]*)/);
  if (!m || !m[1]) return false;
  let qs = m[1];
  try { qs = decodeURIComponent(qs); } catch (e) { /* 已是明文查询串 */ }
  // 兼容双编码场景：解码后仍含 %3D/%26 则再解一层
  if (/%3D|%26/i.test(qs)) { try { qs = decodeURIComponent(qs); } catch (e) {} }
  console.log('[router] efficiency data =', qs);
  localStorage.setItem('okrLoginData', qs);
  const obj = {};
  qs.split('&').forEach(pair => {
    if (!pair) return;
    const i = pair.indexOf('=');
    const k = i >= 0 ? pair.slice(0, i) : pair;
    const v = i >= 0 ? pair.slice(i + 1) : '';
    if (k) { obj[k] = v; localStorage.setItem(k, v); }
  });
  if (obj.UserSysno) localStorage.setItem('CoreEmployeeName', obj.UserSysno);
  if (obj.GovDepSysno) { localStorage.setItem('GovDepSysno', obj.GovDepSysno); localStorage.setItem('CoreDepartmentKey', obj.GovDepSysno); }
  return true;
}

router.beforeEach((to, from, next) => {
  // === 统一权限守卫 ===
  // 路由 meta 里声明了 permCode 或 roleCode 时，统一用 canAccessRoute 检查
  // 规则：精确匹配 || manage 通配符覆盖 read || 角色编码匹配
  const meta = to.meta || {};
  if (meta.permCode || meta.roleCode) {
    if (!canAccessRoute(meta)) {
      // 拒绝访问：回到上一页或首页，并 toast 提示
      console.warn('[router] access denied to', to.path, 'meta=', JSON.stringify(meta));
      try {
        const EventBus = window.__eventBus;
        if (EventBus) EventBus.$emit('toast', '无权访问该页面');
      } catch (_) {}
      if (from.name) {
        next(false); // 停留在上一页
      } else {
        next('/home');
      }
      return;
    }
  }

  // === 兼容性：HomeEntry 里用的 isBossDashboardAccessible 不再需要（组件内部改用共享工具）===

  if (to.name !== 'Efficiency') {
    next();
    return;
  }
  // 进入 efficiency 路由前：优先处理 URL data 入参
  const hasData = applyEfficiencyLoginDataFromHash();
  if (hasData) {
    next();
    return;
  }
  // 无入参：检查本地登录态
  if (localStorage.getItem('UserSysno')) {
    next();
    return;
  }
  console.log('[router] efficiency 无登录态且未带 data 参数，跳转登录页');
  next('/login');
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js